import {
  Box,
  Card,
  ChevronDownIcon,
  ChevronUpIcon,
  Flex,
  FlexProps,
  hexToRgb,
  PercentageValue,
  resolveThemeVariable
} from '@blockanalitica/ui';
import type { Size } from '@blockanalitica/ui/package/types/src/theme/types';
import { ReactNode, useMemo, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';

export interface Breakdown {
  text: ReactNode | string;
  percent: number | string;
  additionalValue?: ReactNode;
}

export interface ProgressBarProps extends FlexProps {
  text: ReactNode;
  color: string;
  percent: number | string;
  additionalValue?: ReactNode;
  breakdown?: Breakdown[];
  height?: Size | string | number;
  muted?: boolean;
  wrapperGap?: string;
}

interface ProgressBarWrapperProps extends FlexProps {
  $color: string;
  $height: string;
}

interface ProgressProps extends FlexProps {
  $color: string;
  $percent: number;
  $height: string;
}

interface ProgressTitleProps extends FlexProps {
  $hasBreakdown: boolean;
}

const ProgressBarWrapper = styled(Flex)<ProgressBarWrapperProps>`
  overflow: hidden;
  border-radius: 9999px;
  box-sizing: border-box;
  width: 100%;

  ${(props) => css`
    background-color: ${props.$color};
    height: ${props.$height};
  `}
`;

const Progress = styled(Flex)<ProgressProps>`
  border-radius: 9999px;
  box-sizing: border-box;

  ${(props) => css`
    width: ${props.$percent}%;
    background-color: ${props.$color};
    height: ${props.$height};
  `}
`;

const ProgressTitle = styled(Flex)<ProgressTitleProps>`
  align-items: center;

  ${(props) => css`
    gap: ${props.theme.space['2xs']};
  `}

  ${(props) =>
    props.$hasBreakdown &&
    css`
      cursor: pointer;

      &:hover {
        color: ${props.theme.colors.linkHover};
      }
    `}
`;

export default function ProgressBar({
  text,
  percent,
  color,
  additionalValue,
  breakdown,
  height = '-1',
  muted = false,
  wrapperGap = '3xs',
  ...rest
}: ProgressBarProps) {
  const theme = useTheme();
  const [showBreakdown, setShowBreakdown] = useState(false);

  let hopefullyHexColor = color;
  if (color[0] != '#') {
    hopefullyHexColor = theme.colors[color];
  }
  const { r, g, b, a } = hexToRgb(hopefullyHexColor);

  const progressHeight = useMemo(
    () =>
      typeof height === 'number'
        ? height + 'px'
        : resolveThemeVariable<Size | string>(height, theme.sizes),
    [height, theme.sizes]
  );

  return (
    <Flex flexDirection="column" gap={wrapperGap}>
      <Flex justifyContent="space-between" alignItems="center">
        <ProgressTitle
          $hasBreakdown={!!breakdown}
          onClick={() => setShowBreakdown(!showBreakdown)}>
          <Box>{text}</Box>
          {breakdown ? (
            showBreakdown ? (
              <ChevronUpIcon size="-1" />
            ) : (
              <ChevronDownIcon size="-1" />
            )
          ) : null}
        </ProgressTitle>
        <Flex gap="2xs" alignItems="center">
          {additionalValue ? (
            <Flex color="muted">({additionalValue})</Flex>
          ) : null}
          <PercentageValue
            value={percent}
            color={muted ? 'muted' : undefined}
          />
        </Flex>
      </Flex>
      <ProgressBarWrapper
        $color={`rgba(${r}, ${g}, ${b}, 0.2)`}
        $height={progressHeight}
        {...rest}>
        <Progress
          $percent={Number(percent) * 100}
          $color={`rgba(${r}, ${g}, ${b}, ${a})`}
          $height={progressHeight}
        />
      </ProgressBarWrapper>

      {breakdown && showBreakdown ? (
        <Card backgroundColor="transparent" marginTop="xs">
          <Flex flexDirection="column" fontSize="-1" gap="3xs">
            {breakdown.map((row, index) => (
              <Flex
                justifyContent="space-between"
                alignItems="center"
                key={index}>
                <Flex>{row.text}</Flex>
                <Flex gap="3xs" alignItems="center">
                  {row.additionalValue ? (
                    <Flex color="muted">({row.additionalValue})</Flex>
                  ) : null}
                  <PercentageValue value={row.percent} />
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Card>
      ) : null}
    </Flex>
  );
}
